/**
 * CampaignsListPage — table of campaigns with status filter.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdoptCampaigns } from '../hooks/useAdvertisingOptimization';
import { ARCHE_PATH } from '../constants';

interface Props {
  holderId: string;
}

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  completed: 'bg-blue-100 text-blue-700',
  archived: 'bg-muted text-muted-foreground',
};

const STATUS_FILTERS = ['all', 'draft', 'active', 'paused', 'completed'] as const;

function centsToUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

export function CampaignsListPage({ holderId }: Props) {
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('all');
  const campaignsQuery = useAdoptCampaigns(
    holderId,
    statusFilter === 'all' ? {} : { status: statusFilter },
  );

  const campaigns = campaignsQuery.data ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Campaigns</h1>
        <Link
          to={`${ARCHE_PATH}/campaigns/new`}
          className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90"
        >
          New campaign
        </Link>
      </header>

      <div className="flex gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-sm capitalize border ${
              statusFilter === s
                ? 'border-primary text-foreground font-medium'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {campaignsQuery.isLoading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
          No campaigns yet. Create one to start testing what acquires users best.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Objective</th>
                <th className="px-4 py-2 font-medium">Total budget</th>
                <th className="px-4 py-2 font-medium">Daily ceiling</th>
                <th className="px-4 py-2 font-medium">Autonomy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {campaigns.map((c) => (
                <tr key={c.id} className="hover:bg-muted/50">
                  <td className="px-4 py-2">
                    <Link
                      to={`${ARCHE_PATH}/campaigns/${c.id}`}
                      className="font-medium hover:underline"
                    >
                      {c.name}
                    </Link>
                    {c.killSwitch && (
                      <span className="ml-2 text-xs rounded bg-rose-100 text-rose-700 px-1.5 py-0.5">
                        kill switch
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs capitalize ${STATUS_BADGE[c.status] ?? ''}`}
                    >
                      {c.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 uppercase text-xs">{c.objective}</td>
                  <td className="px-4 py-2">{centsToUsd(c.totalBudgetCents)}</td>
                  <td className="px-4 py-2">{centsToUsd(c.dailyBudgetCeilingCents)}</td>
                  <td className="px-4 py-2 capitalize">{c.autonomyLevel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
